/**
 * AppErrorBoundary
 *
 * Top-level boundary around the route tree. Its primary job is recovering
 * from stale-chunk failures after a deploy: lazy route chunks are content
 * hashed, so a tab that loaded index.html before a deploy requests chunk
 * files that no longer exist and the dynamic import rejects. A full reload
 * fetches the new index.html and always fixes it, so we reload
 * automatically -- at most once per minute per tab (sessionStorage guard)
 * so a genuine network failure cannot cause a reload loop.
 *
 * When the guard blocks auto-reload (chunk error twice within a minute,
 * i.e. the user is offline or the request is blocked) we render a retry
 * card. Any non-chunk render error renders an error card instead of the
 * unmounted-root white screen.
 */

import { AlertTriangle, RefreshCw } from "lucide-react";
import { Component, ErrorInfo, ReactNode } from "react";
import { trackingService } from "../services/trackingService";
import { TrackingEvents } from "../services/trackingEvents";

const RELOAD_GUARD_KEY = "app_chunk_reload_ts";
const RELOAD_GUARD_WINDOW_MS = 60_000;

/** Matches webpack ChunkLoadError plus the equivalent failures other bundlers/browsers surface. */
export function isChunkLoadError(error: Error): boolean {
  return (
    error.name === "ChunkLoadError" ||
    /Loading (CSS )?chunk [^\s]+ failed/i.test(error.message) ||
    /Failed to fetch dynamically imported module/i.test(error.message) ||
    /error loading dynamically imported module/i.test(error.message) ||
    /Importing a module script failed/i.test(error.message) ||
    /is not a valid JavaScript MIME type/i.test(error.message)
  );
}

/**
 * Check consulted by both render and componentDidCatch so they agree:
 * render shows nothing while componentDidCatch triggers the reload.
 * If sessionStorage is unusable the guard could never persist across
 * reloads, so we refuse to auto-reload (retry card instead of a loop).
 */
function shouldAutoReload(): boolean {
  try {
    const probeKey = "__app_reload_probe__";
    window.sessionStorage.setItem(probeKey, "1");
    window.sessionStorage.removeItem(probeKey);

    const raw = window.sessionStorage.getItem(RELOAD_GUARD_KEY);
    if (!raw) return true;
    const last = Number(raw);
    return !Number.isFinite(last) || Date.now() - last > RELOAD_GUARD_WINDOW_MS;
  } catch {
    return false;
  }
}

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  error: Error | null;
  /** Set when a chunk error wanted an auto-reload but the guard write failed. */
  reloadBlocked: boolean;
}

class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { error: null, reloadBlocked: false };

  static getDerivedStateFromError(error: Error): Partial<AppErrorBoundaryState> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const chunkError = isChunkLoadError(error);
    const wantsReload = chunkError && shouldAutoReload();

    // Persist the guard BEFORE reloading: the probe write is smaller than
    // this one, so at a quota edge it can pass while this write fails.
    // Reloading without a persisted guard could loop forever.
    let guardPersisted = false;
    if (wantsReload) {
      try {
        window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
        guardPersisted = true;
      } catch {
        // Fall through to the retry card below.
      }
    }

    console.error("[AppErrorBoundary] Caught error:", error);
    console.error("Component stack:", errorInfo.componentStack);

    // Small payload on purpose: no stacks, they can contain PII in prop strings.
    try {
      trackingService.track(TrackingEvents.ERROR_BOUNDARY_CAUGHT, {
        boundary_label: "app-root",
        error_name: error.name,
        error_message: error.message,
        chunk_auto_reload: wantsReload && guardPersisted,
      });
      trackingService.exception(error, { boundary_label: "app-root" });
    } catch {
      // Never let analytics failure prevent recovery.
    }

    if (wantsReload) {
      if (guardPersisted) {
        window.location.reload();
      } else {
        // Render returned null expecting a reload; re-render the retry card.
        this.setState({ reloadBlocked: true });
      }
    }
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { error, reloadBlocked } = this.state;
    const { children } = this.props;

    if (!error) return children;

    const chunkError = isChunkLoadError(error);

    // Reload is imminent; render nothing instead of flashing an error card.
    if (chunkError && shouldAutoReload() && !reloadBlocked) return null;

    return (
      <div className="flex min-h-dvh flex-col items-center justify-center bg-gray-50 dark:bg-black p-6 transition-colors duration-300">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 max-w-md w-full text-center">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-red-500 dark:text-red-400" />
          <h3 className="text-lg font-semibold text-red-800 dark:text-red-300 mb-2">
            {chunkError ? "Page failed to load" : "Something went wrong"}
          </h3>
          <p className="text-sm text-red-600 dark:text-red-400 mb-4">
            {chunkError
              ? "We could not load this page. Check your connection and try again."
              : error.message || "An unexpected error occurred"}
          </p>
          <button
            onClick={this.handleReload}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Reload page
          </button>
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
